import type { FamilyMatrixDeal, FamilyMatrixVariant } from 'apis/exclusive-deals';

// Pure model helper: family-matrix response -> price matrix.
// Rows = variants, columns = suppliers (first-appearance order in the deal
// list), one cell per (variant, supplier).

export interface MatrixSupplier {
  id: number;
  name: string | null;
}

export interface MatrixCell {
  dealId: number;
  price: number | null;
  // Ladder rung the price is agreed against; null on rows the BE couldn't
  // resolve a rung for (unmapped legacy deals).
  dealUnit: 'PACK' | 'ITEM' | 'BASE' | null;
  // BE-derived price per base unit; null when a ladder hop is unknown.
  baseUnitPrice: number | null;
  lastModifiedAt: string | null;
  isActive: boolean;
  isLowest: boolean;
}

export interface MatrixRow {
  variant: FamilyMatrixVariant;
  // Keyed by supplierId.
  cells: Record<number, MatrixCell | undefined>;
}

// True when `candidate` should replace `current` for the same (variant,
// supplier) slot: newest lastModifiedAt wins, null loses to any non-null.
function isNewer(candidate: FamilyMatrixDeal, current: FamilyMatrixDeal): boolean {
  if (!candidate.lastModifiedAt) return false;
  if (!current.lastModifiedAt) return true;
  return new Date(candidate.lastModifiedAt).getTime() > new Date(current.lastModifiedAt).getTime();
}

export function buildPriceMatrix(
  variants: FamilyMatrixVariant[],
  deals: FamilyMatrixDeal[]
): { suppliers: MatrixSupplier[]; rows: MatrixRow[] } {
  const suppliers: MatrixSupplier[] = [];
  const seenSuppliers = new Set<number>();
  for (const deal of deals) {
    if (seenSuppliers.has(deal.supplierId)) continue;
    seenSuppliers.add(deal.supplierId);
    suppliers.push({ id: deal.supplierId, name: deal.supplierName });
  }

  // Winning deal per "variantId:supplierId" slot.
  const winners = new Map<string, FamilyMatrixDeal>();
  for (const deal of deals) {
    const key = `${deal.masterProductId}:${deal.supplierId}`;
    const current = winners.get(key);
    if (!current || isNewer(deal, current)) winners.set(key, deal);
  }

  const rows: MatrixRow[] = variants.map((variant) => {
    const cells: Record<number, MatrixCell | undefined> = {};
    for (const supplier of suppliers) {
      const deal = winners.get(`${variant.id}:${supplier.id}`);
      if (!deal) continue;
      cells[supplier.id] = {
        dealId: deal.id,
        price: deal.dealPrice ?? null,
        dealUnit: deal.dealUnit ?? null,
        baseUnitPrice: deal.baseUnitPrice ?? null,
        lastModifiedAt: deal.lastModifiedAt ?? null,
        isActive: deal.isActive,
        isLowest: false,
      };
    }

    // "Lowest" compares per-base-unit prices when EVERY priced cell has one —
    // suppliers may quote different rungs (198/carton vs 35/kg), where the raw
    // figures aren't comparable. Any unconvertible cell in the row drops the
    // whole row back to raw comparison rather than mixing the two scales.
    const priced = Object.values(cells).filter((c): c is MatrixCell => c?.price != null);
    const allComparable = priced.length > 0 && priced.every((c) => c.baseUnitPrice != null);
    const amounts = priced.map((c) => (allComparable ? (c.baseUnitPrice as number) : (c.price as number)));
    if (amounts.length) {
      const min = Math.min(...amounts);
      for (const cell of priced) {
        if ((allComparable ? cell.baseUnitPrice : cell.price) === min) cell.isLowest = true;
      }
    }

    return { variant, cells };
  });

  return { suppliers, rows };
}
